import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { close } from 'ionicons/icons';
import { ReactElement, memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Machine } from '../../../api/graphql/generated';
import { useInactiveMachinesCount } from '../../../api/hooks/team.hooks';
import { IntercomArticle } from '../../../config/intercom';
import { RightAlignedButtons } from '../../button/aligned-buttons';
import { BaseButton } from '../../button/base-button';
import { IntercomHelp } from '../../help/intercom-help';
import { RegisterMachine, RegisterMachineProps } from './register-machine';
import { InactiveMachinePicker } from './register-machine-inactive-picker';

interface RegisterMachineModalProps extends RegisterMachineProps {
  isOpen: boolean;
  onDidDismiss: (machine?: Machine) => void;
}

enum RegistrationState {
  CHECKING_FOR_INACTIVE = 'checkingForInactive',
  PICKING_INACTIVE_MACHINE = 'pickingInactiveMachine',
  REGISTERING_MACHINE = 'regsiteringMachine',
  IDLING = 'idling',
}

const RegisterMachineModal = memo(
  (props: RegisterMachineModalProps): ReactElement => {
    const { isOpen, teamId, machine, onDidDismiss } = props;
    const { i18n } = useTranslation();

    const isNew = machine?.id ? false : true;

    const [registrationState, setRegistrationState] =
      useState<RegistrationState>(RegistrationState.IDLING);

    const onDidDismissInner = useCallback(
      (nextState?: RegistrationState, machine?: Machine) => {
        if (nextState === RegistrationState.IDLING) {
          onDidDismiss(machine);
        }

        if (nextState) {
          setRegistrationState(nextState);
        }
      },
      [onDidDismiss]
    );

    const fetchInactive = useInactiveMachinesCount(teamId);

    const checkInactive = useCallback(async () => {
      const numOfInactive = await fetchInactive();
      if (numOfInactive && numOfInactive > 0) {
        setRegistrationState(RegistrationState.PICKING_INACTIVE_MACHINE);
      } else {
        setRegistrationState(RegistrationState.REGISTERING_MACHINE);
      }
    }, [fetchInactive]);

    useEffect(() => {
      if (isOpen) {
        if (isNew) {
          checkInactive();
        } else {
          setRegistrationState(RegistrationState.REGISTERING_MACHINE);
        }
      }
    }, [checkInactive, isNew, isOpen]);

    const onComplete = useCallback(
      (m: Machine) => {
        onDidDismissInner(RegistrationState.IDLING, m);
      },
      [onDidDismissInner]
    );

    return (
      <>
        <IonModal
          isOpen={
            isOpen &&
            registrationState === RegistrationState.REGISTERING_MACHINE
          }
          canDismiss={true}
          backdropDismiss={false}
        >
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start">
                <IonButton
                  onClick={() => {
                    onDidDismissInner(RegistrationState.IDLING);
                  }}
                >
                  <IonIcon icon={close} size="large" />
                </IonButton>
              </IonButtons>
              <IonTitle>
                {isNew
                  ? i18n.t('common:machine_new')
                  : i18n.t('common:machine_reactivate_this_robot')}
              </IonTitle>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <RegisterMachine
              teamId={teamId}
              machine={machine}
              onComplete={onComplete}
            />
          </IonContent>
        </IonModal>
        <IonModal
          isOpen={
            isOpen &&
            registrationState === RegistrationState.PICKING_INACTIVE_MACHINE
          }
          canDismiss={true}
          backdropDismiss={false}
        >
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start">
                <IonButton
                  onClick={() => {
                    onDidDismissInner(RegistrationState.IDLING);
                  }}
                >
                  <IonIcon icon={close} size="large" />
                </IonButton>
              </IonButtons>
              <IonTitle> {i18n.t('common:machine_reuse_inactive')}</IonTitle>
              <IonButtons slot="end">
                <IntercomHelp articleId={IntercomArticle.MANAGING_ROBOTS} />
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <InactiveMachinePicker
              teamId={teamId}
              onSelect={() => {
                onDidDismissInner(RegistrationState.IDLING);
              }}
            />
          </IonContent>
          <IonFooter>
            <IonToolbar>
              <RightAlignedButtons>
                <BaseButton
                  buttonProps={{
                    fill: 'outline',
                    onClick: () => {
                      setRegistrationState(
                        RegistrationState.REGISTERING_MACHINE
                      );
                    },
                  }}
                >
                  {i18n.t('common:machine_new')}
                </BaseButton>
              </RightAlignedButtons>
            </IonToolbar>
          </IonFooter>
        </IonModal>
      </>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.isOpen === nextProps.isOpen &&
      prevProps.teamId === nextProps.teamId &&
      prevProps.machine?.id === nextProps.machine?.id &&
      prevProps.onDidDismiss === nextProps.onDidDismiss
    );
  }
);

RegisterMachineModal.displayName = 'RegisterMachineModal';

export { RegisterMachineModal };
